import React, { useState } from 'react';
import { Edit2, Check, X, Image as ImageIcon, Upload, User, Phone, IdCard, MapPin, Building2, Package, DollarSign, Calendar } from 'lucide-react';
import axios from 'axios';
import ImageCarouselModal from './ImageCarouselModal';
import ImageUploadModal from './ImageUploadModal';

const API_URL = process.env.REACT_APP_API_URL;

function ProvinciaDeliveriesTable({ deliveries, onDeliveryUpdate, onRefresh }) {
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);

  // Image modals
  const [selectedImages, setSelectedImages] = useState(null);
  const [uploadingFor, setUploadingFor] = useState(null);

  const statusOptions = [
    { value: 'pending', label: 'Pendiente', color: 'bg-orange-100 text-orange-700' },
    { value: 'processing', label: 'En Proceso', color: 'bg-blue-100 text-blue-700' },
    { value: 'shipped', label: 'Enviado', color: 'bg-purple-100 text-purple-700' },
    { value: 'delivered', label: 'Entregado', color: 'bg-green-100 text-green-700' },
    { value: 'cancelled', label: 'Cancelado', color: 'bg-red-100 text-red-700' }
  ];

  function startEdit(deliveryId, field, currentValue) {
    setEditingField({ deliveryId, field });
    setEditValue(currentValue || '');
  }

  function cancelEdit() {
    setEditingField(null);
    setEditValue('');
  }

  async function saveEdit(deliveryId, field) {
    setSaving(true);
    try {
      const response = await axios.patch(
        `${API_URL}/api/admin/provincia-deliveries/${deliveryId}`,
        { [field]: editValue }
      );
      onDeliveryUpdate(response.data.delivery);
      setEditingField(null);
      setEditValue('');
    } catch (error) {
      console.error('Error updating delivery:', error);
      alert('Error al actualizar. Por favor intenta de nuevo.');
    } finally {
      setSaving(false);
    }
  }

  function getStatusLabel(status) {
    const option = statusOptions.find(o => o.value === status);
    return option ? option.label : status;
  }

  function getStatusColor(status) {
    const option = statusOptions.find(o => o.value === status);
    return option ? option.color : 'bg-slate-100 text-slate-700';
  }

  function handleViewImages(delivery) {
    setSelectedImages({
      images: delivery.images || [],
      deliveryId: delivery.id,
      recipientName: delivery.recipient_name
    });
  }

  function handleUploadImage(delivery) {
    setUploadingFor(delivery);
  }

  async function handleImageUploadComplete(imageUrl) {
    setUploadingFor(null);
    onRefresh();
  }

  function truncateText(text, maxLength = 15) {
    if (!text) return '-';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  }

  return (
    <>
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-brand-primary text-white">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold font-montserrat">ID</th>
                <th className="px-4 py-3 text-left text-xs font-semibold font-montserrat">Usuario</th>
                <th className="px-4 py-3 text-left text-xs font-semibold font-montserrat">Nombre</th>
                <th className="px-4 py-3 text-left text-xs font-semibold font-montserrat">DNI</th>
                <th className="px-4 py-3 text-left text-xs font-semibold font-montserrat">Teléfono</th>
                <th className="px-4 py-3 text-left text-xs font-semibold font-montserrat">Notas</th>
                <th className="px-4 py-3 text-left text-xs font-semibold font-montserrat">Provincia</th>
                <th className="px-4 py-3 text-left text-xs font-semibold font-montserrat">Agencia</th>
                <th className="px-4 py-3 text-left text-xs font-semibold font-montserrat">Producto</th>
                <th className="px-4 py-3 text-left text-xs font-semibold font-montserrat">Cargo</th>
                <th className="px-4 py-3 text-left text-xs font-semibold font-montserrat">Anticipo</th>
                <th className="px-4 py-3 text-left text-xs font-semibold font-montserrat">Código</th>
                <th className="px-4 py-3 text-left text-xs font-semibold font-montserrat">Estado</th>
                <th className="px-4 py-3 text-left text-xs font-semibold font-montserrat">Cobrado</th>
                <th className="px-4 py-3 text-center text-xs font-semibold font-montserrat">Imágenes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {deliveries.map((delivery) => (
                <tr key={delivery.id} className="hover:bg-slate-50 transition-colors">
                  {/* ID */}
                  <td className="px-4 py-3 text-sm font-semibold text-slate-900 font-montserrat">
                    #{delivery.id}
                  </td>

                  {/* User ID */}
                  <td className="px-4 py-3 text-sm text-slate-600 font-montserrat">
                    <div className="flex items-center gap-1">
                      <User className="w-4 h-4 text-slate-400" />
                      {delivery.user_id}
                    </div>
                  </td>

                  {/* Recipient Name */}
                  <td className="px-4 py-3 text-sm text-slate-900 font-montserrat">
                    {delivery.recipient_name}
                  </td>

                  {/* DNI */}
                  <td className="px-4 py-3 text-sm text-slate-600 font-montserrat">
                    {delivery.recipient_dni || '-'}
                  </td>

                  {/* Phone */}
                  <td className="px-4 py-3 text-sm text-slate-600 font-montserrat">
                    <div className="flex items-center gap-1">
                      <Phone className="w-4 h-4 text-slate-400" />
                      {delivery.recipient_phone}
                    </div>
                  </td>

                  {/* Notes */}
                  <td className="px-4 py-3 text-sm text-slate-600 font-montserrat">
                    <span title={delivery.notes}>
                      {truncateText(delivery.notes, 15)}
                    </span>
                  </td>

                  {/* Province */}
                  <td className="px-4 py-3 text-sm font-montserrat">
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs font-semibold">
                      <MapPin className="w-3 h-3" />
                      {delivery.province}
                    </span>
                  </td>

                  {/* Agency Address */}
                  <td className="px-4 py-3 text-sm text-slate-600 font-montserrat">
                    <span title={delivery.agency_address}>
                      {truncateText(delivery.agency_address, 20)}
                    </span>
                  </td>

                  {/* Product */}
                  <td className="px-4 py-3 text-sm text-slate-600 font-montserrat">
                    <span title={delivery.product}>
                      {truncateText(delivery.product, 20)}
                    </span>
                  </td>

                  {/* Charge */}
                  <td className="px-4 py-3 text-sm font-semibold text-slate-900 font-montserrat">
                    S/ {parseFloat(delivery.charge || 0).toFixed(2)}
                  </td>

                  {/* Deposit Advance */}
                  <td className="px-4 py-3 text-sm font-semibold text-blue-600 font-montserrat">
                    S/ {parseFloat(delivery.deposit_advance || 0).toFixed(2)}
                  </td>

                  {/* Agency Pickup Code - Editable */}
                  <td className="px-4 py-3">
                    {editingField?.deliveryId === delivery.id && editingField?.field === 'agency_pickup_code' ? (
                      <div className="flex items-center gap-1">
                        <input
                          type="text"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          className="w-24 px-2 py-1 text-xs border-2 border-brand-primary rounded focus:outline-none font-montserrat"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') saveEdit(delivery.id, 'agency_pickup_code');
                            if (e.key === 'Escape') cancelEdit();
                          }}
                        />
                        <button
                          onClick={() => saveEdit(delivery.id, 'agency_pickup_code')}
                          disabled={saving}
                          className="p-1 text-green-600 hover:bg-green-50 rounded"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                        <button
                          onClick={cancelEdit}
                          disabled={saving}
                          className="p-1 text-red-600 hover:bg-red-50 rounded"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-slate-900 font-montserrat">
                          {delivery.agency_pickup_code || '-'}
                        </span>
                        <button
                          onClick={() => startEdit(delivery.id, 'agency_pickup_code', delivery.agency_pickup_code)}
                          className="p-1 text-slate-400 hover:text-brand-primary hover:bg-brand-primary/10 rounded transition-colors"
                        >
                          <Edit2 className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                  </td>

                  {/* Status - Editable */}
                  <td className="px-4 py-3">
                    {editingField?.deliveryId === delivery.id && editingField?.field === 'status' ? (
                      <div className="flex items-center gap-1">
                        <select
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          className="text-xs border-2 border-brand-primary rounded focus:outline-none font-montserrat px-2 py-1"
                          autoFocus
                        >
                          {statusOptions.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                        <button
                          onClick={() => saveEdit(delivery.id, 'status')}
                          disabled={saving}
                          className="p-1 text-green-600 hover:bg-green-50 rounded"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                        <button
                          onClick={cancelEdit}
                          disabled={saving}
                          className="p-1 text-red-600 hover:bg-red-50 rounded"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-semibold font-montserrat ${getStatusColor(delivery.status)}`}>
                          {getStatusLabel(delivery.status)}
                        </span>
                        <button
                          onClick={() => startEdit(delivery.id, 'status', delivery.status)}
                          className="p-1 text-slate-400 hover:text-brand-primary hover:bg-brand-primary/10 rounded transition-colors"
                        >
                          <Edit2 className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                  </td>

                  {/* Charged - Editable */}
                  <td className="px-4 py-3">
                    {editingField?.deliveryId === delivery.id && editingField?.field === 'charged' ? (
                      <div className="flex items-center gap-1">
                        <input
                          type="number"
                          step="0.01"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          className="w-20 px-2 py-1 text-xs border-2 border-brand-primary rounded focus:outline-none font-montserrat"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') saveEdit(delivery.id, 'charged');
                            if (e.key === 'Escape') cancelEdit();
                          }}
                        />
                        <button
                          onClick={() => saveEdit(delivery.id, 'charged')}
                          disabled={saving}
                          className="p-1 text-green-600 hover:bg-green-50 rounded"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                        <button
                          onClick={cancelEdit}
                          disabled={saving}
                          className="p-1 text-red-600 hover:bg-red-50 rounded"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-green-600 font-montserrat">
                          S/ {parseFloat(delivery.charged || 0).toFixed(2)}
                        </span>
                        <button
                          onClick={() => startEdit(delivery.id, 'charged', delivery.charged || 0)}
                          className="p-1 text-slate-400 hover:text-brand-primary hover:bg-brand-primary/10 rounded transition-colors"
                        >
                          <Edit2 className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                  </td>

                  {/* Images */}
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-2">
                      <button
                        onClick={() => handleViewImages(delivery)}
                        disabled={!delivery.images || delivery.images.length === 0}
                        className="p-2 text-brand-primary hover:bg-brand-primary/10 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed group relative"
                        title="Ver imágenes"
                      >
                        <ImageIcon className="w-4 h-4" />
                        {delivery.images && delivery.images.length > 0 && (
                          <span className="absolute -top-1 -right-1 bg-brand-primary text-white text-xs rounded-full w-4 h-4 flex items-center justify-center font-montserrat">
                            {delivery.images.length}
                          </span>
                        )}
                      </button>
                      <button
                        onClick={() => handleUploadImage(delivery)}
                        className="p-2 text-brand-gold hover:bg-brand-gold/10 rounded-lg transition-colors"
                        title="Subir imagen"
                      >
                        <Upload className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Image Carousel Modal */}
      {selectedImages && (
        <ImageCarouselModal
          images={selectedImages.images}
          deliveryId={selectedImages.deliveryId}
          recipientName={selectedImages.recipientName}
          onClose={() => setSelectedImages(null)}
        />
      )}

      {/* Image Upload Modal */}
      {uploadingFor && (
        <ImageUploadModal
          delivery={uploadingFor}
          onClose={() => setUploadingFor(null)}
          onUploadComplete={handleImageUploadComplete}
        />
      )}
    </>
  );
}

export default ProvinciaDeliveriesTable;